import React, { useState, useEffect, useCallback } from 'react';
import {
  MdError, MdWarning, MdInfo, MdBugReport, MdRefresh,
  MdSearch, MdFilterList, MdCheckCircle, MdCancel,
  MdExpandMore, MdExpandLess, MdDelete, MdDone,
  MdReplay, MdContentCopy, MdAccessTime
} from 'react-icons/md';
import { toast } from 'react-toastify';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import { errorLogsApi } from '../../../api/errorLogsApi';
import '../Admin.css';
import './ErrorLogs.css';

const SEVERITY_CONFIG = {
  CRITICAL: { icon: MdError, color: '#dc2626', bg: '#fef2f2', label: 'Critical' },
  ERROR: { icon: MdBugReport, color: '#ea580c', bg: '#fff7ed', label: 'Error' },
  WARNING: { icon: MdWarning, color: '#ca8a04', bg: '#fefce8', label: 'Warning' },
  INFO: { icon: MdInfo, color: '#0284c7', bg: '#f0f9ff', label: 'Info' }
};

function ErrorLogs() {
  const [errors, setErrors] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Filters
  const [filters, setFilters] = useState({
    severity: '',
    resolved: '',
    search: '',
    startDate: '',
    endDate: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Selected errors for bulk actions
  const [selectedErrors, setSelectedErrors] = useState(new Set());
  const [expandedError, setExpandedError] = useState(null);

  // Modal states
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolvingErrorId, setResolvingErrorId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingErrorId, setDeletingErrorId] = useState(null);

  // Fetch error logs
  const fetchErrorLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await errorLogsApi.getErrorLogs({
        page: currentPage,
        size: pageSize,
        severity: filters.severity || undefined,
        resolved: filters.resolved !== '' ? filters.resolved === 'true' : undefined,
        search: filters.search || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
      });

      if (response.data) {
        setErrors(response.data.content || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (error) {
      console.error('Error fetching error logs:', error);
      toast.error('Failed to fetch error logs');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, filters]);

  // Fetch dashboard stats
  const fetchDashboardStats = useCallback(async () => {
    try {
      setIsDashboardLoading(true);
      const response = await errorLogsApi.getDashboardStats();
      if (response.data) {
        setDashboardStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setIsDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchErrorLogs();
  }, [fetchErrorLogs]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(0);
  };

  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, search: value }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.search !== '') {
        setCurrentPage(0);
        fetchErrorLogs();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Handle resolve error
  const handleResolve = async () => {
    try {
      if (selectedErrors.size > 1) {
        await errorLogsApi.bulkResolve(Array.from(selectedErrors), resolveNotes);
        toast.success(`${selectedErrors.size} errors marked as resolved`);
      } else {
        await errorLogsApi.resolveError(resolvingErrorId, resolveNotes);
        toast.success('Error marked as resolved');
      }
      setShowResolveModal(false);
      setResolveNotes('');
      setResolvingErrorId(null);
      setSelectedErrors(new Set());
      fetchErrorLogs();
      fetchDashboardStats();
    } catch (error) {
      toast.error('Failed to resolve error');
    }
  };

  // Handle reopen error
  const handleReopen = async (id) => {
    try {
      await errorLogsApi.reopenError(id);
      toast.success('Error reopened');
      fetchErrorLogs();
      fetchDashboardStats();
    } catch (error) {
      toast.error('Failed to reopen error');
    }
  };

  // Handle delete error
  const handleDelete = async () => {
    try {
      await errorLogsApi.deleteError(deletingErrorId);
      toast.success('Error log deleted');
      setShowDeleteModal(false);
      setDeletingErrorId(null);
      fetchErrorLogs();
      fetchDashboardStats();
    } catch (error) {
      toast.error('Failed to delete error');
    }
  };

  // Toggle error selection
  const toggleErrorSelection = (id) => {
    const newSelected = new Set(selectedErrors);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedErrors(newSelected);
  };

  // Select all errors
  const toggleSelectAll = () => {
    if (selectedErrors.size === errors.length) {
      setSelectedErrors(new Set());
    } else {
      setSelectedErrors(new Set(errors.map(e => e.id)));
    }
  };

  // Copy stack trace to clipboard
  const copyStackTrace = (stackTrace) => {
    navigator.clipboard.writeText(stackTrace);
    toast.success('Stack trace copied to clipboard');
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Format relative time
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // Render severity badge
  const renderSeverityBadge = (severity) => {
    const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.INFO;
    const Icon = config.icon;
    return (
      <span
        className="severity-badge"
        style={{ backgroundColor: config.bg, color: config.color }}
      >
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  return (
    <div className="error-logs-page">
      <PageHeader
        title="System Error Logs"
        subtitle="Monitor and manage application errors"
      />

      {/* Dashboard Stats */}
      <div className="error-stats-grid">
        <div className="stat-card critical">
          <div className="stat-icon">
            <MdError size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">
              {isDashboardLoading ? '...' : dashboardStats?.criticalUnresolvedCount || 0}
            </span>
            <span className="stat-label">Critical Unresolved</span>
          </div>
        </div>

        <div className="stat-card error">
          <div className="stat-icon">
            <MdBugReport size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">
              {isDashboardLoading ? '...' : dashboardStats?.errorsLast24Hours || 0}
            </span>
            <span className="stat-label">Errors (24h)</span>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">
            <MdWarning size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">
              {isDashboardLoading ? '...' : dashboardStats?.unresolvedBySeverity?.WARNING || 0}
            </span>
            <span className="stat-label">Warnings Unresolved</span>
          </div>
        </div>

        <div className="stat-card total">
          <div className="stat-icon">
            <MdAccessTime size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{totalElements}</span>
            <span className="stat-label">Total Logged</span>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="error-actions-bar">
        <div className="search-section">
          <div className="search-input-wrapper">
            <MdSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search errors..."
              value={filters.search}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          <button
            className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <MdFilterList />
            Filters
          </button>
          <button className="refresh-btn" onClick={() => { fetchErrorLogs(); fetchDashboardStats(); }}>
            <MdRefresh />
            Refresh
          </button>
        </div>

        {selectedErrors.size > 0 && (
          <div className="bulk-actions">
            <span className="selected-count">{selectedErrors.size} selected</span>
            <button
              className="bulk-resolve-btn"
              onClick={() => {
                setResolvingErrorId(null);
                setShowResolveModal(true);
              }}
            >
              <MdCheckCircle />
              Resolve Selected
            </button>
          </div>
        )}
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Severity</label>
            <select
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
            >
              <option value="">All Severities</option>
              <option value="CRITICAL">Critical</option>
              <option value="ERROR">Error</option>
              <option value="WARNING">Warning</option>
              <option value="INFO">Info</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.resolved}
              onChange={(e) => handleFilterChange('resolved', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="false">Unresolved</option>
              <option value="true">Resolved</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="datetime-local"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="datetime-local"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>

          <button
            className="clear-filters-btn"
            onClick={() => setFilters({ severity: '', resolved: '', search: '', startDate: '', endDate: '' })}
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Error List */}
      <div className="error-list-container">
        {isLoading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading error logs...</p>
          </div>
        ) : errors.length === 0 ? (
          <div className="empty-state">
            <MdCheckCircle size={48} className="empty-icon" />
            <h3>No errors found</h3>
            <p>Great job! No errors match your current filters.</p>
          </div>
        ) : (
          <>
            <div className="error-list-header">
              <div className="header-checkbox">
                <input
                  type="checkbox"
                  checked={selectedErrors.size === errors.length && errors.length > 0}
                  onChange={toggleSelectAll}
                />
              </div>
              <div className="header-severity">Severity</div>
              <div className="header-message">Error Details</div>
              <div className="header-time">Time</div>
              <div className="header-status">Status</div>
              <div className="header-actions">Actions</div>
            </div>

            {errors.map((error) => (
              <div key={error.id} className={`error-item ${expandedError === error.id ? 'expanded' : ''}`}>
                <div className="error-row" onClick={() => setExpandedError(expandedError === error.id ? null : error.id)}>
                  <div className="error-checkbox" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedErrors.has(error.id)}
                      onChange={() => toggleErrorSelection(error.id)}
                    />
                  </div>
                  <div className="error-severity">
                    {renderSeverityBadge(error.severity)}
                  </div>
                  <div className="error-message">
                    <span className="error-type">{error.errorType}</span>
                    <span className="error-text">{error.message}</span>
                    {error.requestUri && (
                      <span className="error-uri">
                        <code>{error.httpMethod} {error.requestUri}</code>
                      </span>
                    )}
                  </div>
                  <div className="error-time">
                    <span className="relative-time">{formatRelativeTime(error.timestamp)}</span>
                    <span className="full-time">{formatDate(error.timestamp)}</span>
                  </div>
                  <div className="error-status">
                    {error.resolved ? (
                      <span className="status-badge resolved">
                        <MdCheckCircle size={14} />
                        Resolved
                      </span>
                    ) : (
                      <span className="status-badge unresolved">
                        <MdCancel size={14} />
                        Open
                      </span>
                    )}
                  </div>
                  <div className="error-actions" onClick={(e) => e.stopPropagation()}>
                    {expandedError === error.id ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
                  </div>
                </div>

                {expandedError === error.id && (
                  <div className="error-details">
                    <div className="details-grid">
                      <div className="detail-section">
                        <h4>Request Info</h4>
                        <div className="detail-row">
                          <span className="detail-label">URI:</span>
                          <span className="detail-value">{error.requestUri || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Method:</span>
                          <span className="detail-value">{error.httpMethod || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">HTTP Status:</span>
                          <span className="detail-value">{error.httpStatus || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Client IP:</span>
                          <span className="detail-value">{error.clientIp || '-'}</span>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h4>User Info</h4>
                        <div className="detail-row">
                          <span className="detail-label">User Email:</span>
                          <span className="detail-value">{error.userEmail || 'Anonymous'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">User Role:</span>
                          <span className="detail-value">{error.userRole || '-'}</span>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h4>Code Location</h4>
                        <div className="detail-row">
                          <span className="detail-label">Controller:</span>
                          <span className="detail-value">{error.controllerClass || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Method:</span>
                          <span className="detail-value">{error.controllerMethod || '-'}</span>
                        </div>
                      </div>

                      {error.resolved && (
                        <div className="detail-section">
                          <h4>Resolution</h4>
                          <div className="detail-row">
                            <span className="detail-label">Resolved By:</span>
                            <span className="detail-value">{error.resolvedBy || '-'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Resolved At:</span>
                            <span className="detail-value">{formatDate(error.resolvedAt)}</span>
                          </div>
                          {error.resolutionNotes && (
                            <div className="detail-row">
                              <span className="detail-label">Notes:</span>
                              <span className="detail-value">{error.resolutionNotes}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {error.stackTrace && (
                      <div className="stack-trace-section">
                        <div className="stack-trace-header">
                          <h4>Stack Trace</h4>
                          <button
                            className="copy-btn"
                            onClick={() => copyStackTrace(error.stackTrace)}
                          >
                            <MdContentCopy size={16} />
                            Copy
                          </button>
                        </div>
                        <pre className="stack-trace">{error.stackTrace}</pre>
                      </div>
                    )}

                    <div className="error-detail-actions">
                      {!error.resolved ? (
                        <button
                          className="resolve-btn"
                          onClick={() => {
                            setResolvingErrorId(error.id);
                            setSelectedErrors(new Set());
                            setShowResolveModal(true);
                          }}
                        >
                          <MdDone />
                          Mark Resolved
                        </button>
                      ) : (
                        <button
                          className="reopen-btn"
                          onClick={() => handleReopen(error.id)}
                        >
                          <MdReplay />
                          Reopen
                        </button>
                      )}
                      <button
                        className="delete-btn"
                        onClick={() => {
                          setDeletingErrorId(error.id);
                          setShowDeleteModal(true);
                        }}
                      >
                        <MdDelete />
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage + 1}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page - 1)}
        />
      )}

      {/* Resolve Modal */}
      {showResolveModal && (
        <div className="modal-overlay" onClick={() => setShowResolveModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Resolve Error{selectedErrors.size > 1 ? 's' : ''}</h3>
            <p>
              {selectedErrors.size > 1
                ? `Mark ${selectedErrors.size} errors as resolved?`
                : 'Add optional notes about the resolution:'}
            </p>
            <textarea
              value={resolveNotes}
              onChange={(e) => setResolveNotes(e.target.value)}
              placeholder="Resolution notes (optional)..."
              rows={4}
            />
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowResolveModal(false)}>
                Cancel
              </button>
              <button className="confirm-btn" onClick={handleResolve}>
                <MdCheckCircle />
                Resolve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Error Log</h3>
            <p>Are you sure you want to permanently delete this error log? This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="delete-confirm-btn" onClick={handleDelete}>
                <MdDelete />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ErrorLogs;
